import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  Grid,
  IconButton,
  Card,
  CardContent,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Tabs,
  Tab,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import PaymentsIcon from "@mui/icons-material/Payments";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import { fetchJson } from "../../../utils/fetchJson";
import { UserContext } from "../../../contexts/UserContext";
import { useTheme } from "@mui/material/styles";
import ErrorBoundary from "../../../components/ErrorBoundary";
import SchoolFormModal from "../../../components/SchoolFormModal";

function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ("00" + value.toString(16)).substr(-2);
  }
  return color;
}

function ActionsCell({
  params,
  openEditDialog,
  handleDelete,
  handleView,
  handleSuspend,
  handleRelieveSuspension,
  handleRenewSubscription,
  handleUnsubscribe,
  openPricingDialog,
  user,
  theme,
  t,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onAction = (action) => {
    handleClose();
    action();
  };

  const canFeature = React.useCallback((permKey) => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes(permKey);
  }, [user]);

  const canManageSchools = canFeature('schools');

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5,
            width: '20ch',
          },
        }}
      >
        <MenuItem onClick={() => onAction(() => handleView(params.row))}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>{t('common.view')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => onAction(() => openEditDialog(params.row))} disabled={!canManageSchools}>
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => onAction(() => handleDelete(params.row.id))} disabled={!canManageSchools}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
        {params?.row?.status === 'suspended' ? (
          <MenuItem onClick={() => onAction(() => handleRelieveSuspension(params.row.id))} disabled={!canManageSchools}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{t('schoolsPage.reactivate')}</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => onAction(() => handleSuspend(params.row.id))} disabled={!canManageSchools}>
            <ListItemIcon>
              <BlockIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>{t('schoolsPage.suspension')}</ListItemText>
          </MenuItem>
        )}

        {/* Terms are set per school, so they belong on the school's own row
            rather than on the plan every school shares. Available whatever the
            subscription status: terms are usually agreed before the first
            invoice, not after one has expired. */}
        <MenuItem onClick={() => onAction(() => openPricingDialog(params.row))} disabled={!canManageSchools}>
          <ListItemIcon>
            <PaymentsIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>{t('schoolsPage.pricing.action', 'Subscription pricing')}</ListItemText>
        </MenuItem>

        {String(params?.row?.subscriptionStatus || '').toLowerCase() === 'expired' && (
          <MenuItem onClick={() => onAction(() => handleRenewSubscription(params.row.id))} disabled={!canManageSchools}>
            <ListItemIcon>
              <LockIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>{t('schoolsPage.renewSubscription')}</ListItemText>
          </MenuItem>
        )}

        {['active', 'in_grace'].includes(String(params?.row?.subscriptionStatus || '').toLowerCase()) && (
          <MenuItem onClick={() => onAction(() => handleUnsubscribe(params.row.id))} disabled={!canManageSchools}>
            <ListItemIcon>
              <LockIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('schoolsPage.unsubscribe')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

export default function SchoolDirectoryPage() {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  }, [t]);
  const user = useContext(UserContext);
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null); // 'suspend' | 'reactivate'
  const [schoolToToggleStatus, setSchoolToToggleStatus] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [schoolToView, setSchoolToView] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [metrics, setMetrics] = useState({
    total: 0,
    active: 0,
    pending: 0,
    needsAttention: 0,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState('all');

  const normalizeSubscriptionStatus = (s) => {
    const v = String(s || '').toLowerCase().trim();
    if (!v) return 'active';
    if (v === 'in_grace') return 'in_grace';
    if (v === 'expired') return 'expired';
    if (v === 'suspended') return 'suspended';
    return v;
  };

  const [schoolAdmins, setSchoolAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState('');
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [adminToImpersonate, setAdminToImpersonate] = useState(null);
  const [impersonateSchoolId, setImpersonateSchoolId] = useState('');

  useEffect(() => {
    try {
      const storedTab = localStorage.getItem('superAdminSchoolsActiveTab');
      if (storedTab != null) {
        const n = Number(storedTab);
        setActiveTab(Number.isFinite(n) ? n : 0);
        localStorage.removeItem('superAdminSchoolsActiveTab');
      }
    } catch (_) {}
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadSchools = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (subscriptionStatusFilter && subscriptionStatusFilter !== 'all') {
        params.set('subscriptionStatus', subscriptionStatusFilter);
      }
      const data = await fetchJson(
        `/schools?${params.toString()}`
      );

      // Transform data to include admin details
      const transformedSchools = (data.schools || []).map((school) => ({
        ...school,
        adminName: school.adminName || "N/A",
        adminEmail: school.adminEmail || "N/A",
        adminPhone: school.adminPhone || "N/A",
        subscriptionStatus: normalizeSubscriptionStatus(school.subscriptionStatus || school.status),
      }));

      setSchools(transformedSchools);
      setTotalCount(data.total || 0);
    } catch (e) {
      setError(`Failed to load schools: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchQuery, subscriptionStatusFilter]);

  const loadSchoolAdmins = useCallback(async () => {
    if (user?.role !== 'SystemAdmin') return;
    setAdminsLoading(true);
    setAdminsError('');
    try {
      const data = await fetchJson('/api/school-admins');
      setSchoolAdmins(Array.isArray(data) ? data : (data?.schoolAdmins || []));
    } catch (e) {
      setAdminsError(tr('schoolAdmins.errors.load', 'Failed to load school admins'));
    } finally {
      setAdminsLoading(false);
    }
  }, [tr, user?.role]);

  useEffect(() => {
    loadSchools();
    loadMetrics();
  }, [paginationModel, loadSchools, searchQuery, subscriptionStatusFilter]);

  // Per-school subscription terms. `pricingSchool` doubles as the open flag so
  // the dialog can never be open without a school to apply to.
  const [pricingSchool, setPricingSchool] = useState(null);
  const [pricingMode, setPricingMode] = useState('');
  const [pricingAmount, setPricingAmount] = useState('');
  const [pricingNote, setPricingNote] = useState('');
  const [pricingSaving, setPricingSaving] = useState(false);

  const openPricingDialog = async (row) => {
    setPricingSchool(row);
    setPricingMode('');
    setPricingAmount('');
    setPricingNote('');
    // The grid row carries no terms, so they are read from the school itself —
    // otherwise the dialog would open blank over terms that already exist and
    // an admin who saved would wipe them.
    try {
      const res = await fetchJson(`/schools/${row.id}/details`);
      const school = res?.school || res?.data || res;
      setPricingMode(school?.subscriptionBillingMode || '');
      setPricingAmount(school?.subscriptionPrice == null ? '' : String(school.subscriptionPrice));
      setPricingNote(school?.subscriptionPriceNote || '');
    } catch (_) { /* a school with no terms yet is the normal case */ }
  };

  const savePricing = async () => {
    if (!pricingSchool) return;
    setPricingSaving(true);
    try {
      const body = pricingMode
        ? { billingMode: pricingMode, price: Number(pricingAmount || 0), note: pricingNote || null }
        : { note: pricingNote || null };   // no mode clears the terms
      await fetchJson(`/schools/${pricingSchool.id}/subscription-pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      showSnackbar(
        pricingMode
          ? tr('schoolsPage.pricing.saved', 'Subscription pricing saved for this school')
          : tr('schoolsPage.pricing.cleared', 'Pricing cleared — this school returns to the standard rate'),
        'success',
      );
      setPricingSchool(null);
      loadSchools();
    } catch (e) {
      setError(e?.data?.error || e?.message
        || tr('schoolsPage.pricing.error', 'Could not save the pricing'));
    } finally {
      setPricingSaving(false);
    }
  };

  const handleRenewSubscription = async (id) => {
    const prevSchools = schools;
    setError('');
    try {
      await fetchJson(`/schools/${id}/renew-subscription`, { method: 'PUT' });
      showSnackbar(tr('schoolsPage.renewSubscriptionSuccess', 'Subscription renewed successfully'), 'success');
      loadSchools();
      loadMetrics();
    } catch (e) {
      setSchools(prevSchools);
      const details = (e && e.data && (e.data.details || e.data.error))
        ? (e.data.details || e.data.error)
        : (e?.message || null);
      const baseMsg = tr('schoolsPage.renewSubscriptionError', 'Failed to renew subscription');
      setError(details ? `${baseMsg}: ${details}` : baseMsg);
    }
  };

  const handleUnsubscribe = async (id) => {
    const prevSchools = schools;
    setError('');
    try {
      await fetchJson(`/schools/${id}/unsubscribe`, { method: 'PUT' });
      showSnackbar(tr('schoolsPage.unsubscribeSuccess', 'School unsubscribed successfully'), 'success');
      loadSchools();
      loadMetrics();
    } catch (e) {
      setSchools(prevSchools);
      const details = (e && e.data && (e.data.details || e.data.error))
        ? (e.data.details || e.data.data?.error || e.data.details || e.data.error)
        : (e?.message || null);
      const baseMsg = tr('schoolsPage.unsubscribeError', 'Failed to unsubscribe school');
      setError(details ? `${baseMsg}: ${details}` : baseMsg);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      loadSchoolAdmins();
    }
  }, [activeTab, loadSchoolAdmins]);

  const loadMetrics = async () => {
    try {
      const data = await fetchJson("/schools/metrics");
      setMetrics(data);
    } catch (e) {
      console.error("Failed to load metrics:", e);
    }
  };

  const handleConfirmDelete = async () => {
    if (!schoolToDelete) return;

    const id = schoolToDelete.id;
    const deletedStatus = schoolToDelete.status;
    const prevSchools = schools;
    const prevTotalCount = totalCount;
    const prevMetrics = metrics;

    setDeleteDialogOpen(false);
    setSchoolToDelete(null);
    setError("");

    setSchools((prev) => prev.filter((s) => s.id !== id));
    setTotalCount((prev) => Math.max(0, (prev || 0) - 1));
    setMetrics((prev) => {
      const next = { ...(prev || {}) };
      next.total = Math.max(0, (next.total || 0) - 1);
      if (deletedStatus === "active") next.active = Math.max(0, (next.active || 0) - 1);
      if (deletedStatus === "suspended") next.needsAttention = Math.max(0, (next.needsAttention || 0) - 1);
      return next;
    });

    try {
      await fetchJson(`/schools/${id}`, {
        method: "DELETE",
      });
      showSnackbar(tr("schoolsPage.deleteSuccess", "School deleted successfully"), "success");
      loadSchools();
      loadMetrics();
    } catch (e) {
      setSchools(prevSchools);
      setTotalCount(prevTotalCount);
      setMetrics(prevMetrics);
      const details = (e && e.data && (e.data.details || e.data.error))
        ? (e.data.details || e.data.error)
        : (e?.message || null);
      const baseMsg = tr("schoolsPage.deleteError", "Failed to delete school");
      setError(details ? `${baseMsg}: ${details}` : baseMsg);
    }
  };

  const openStatusDialog = (action, id) => {
    const target = schools.find((s) => s.id === id) || { id };
    setStatusAction(action);
    setSchoolToToggleStatus(target);
    setStatusDialogOpen(true);
  };

  const handleSuspend = (id) => openStatusDialog('suspend', id);
  const handleRelieveSuspension = (id) => openStatusDialog('reactivate', id);

  const openImpersonateDialog = (admin) => {
    const fallbackSchoolId =
      (admin && admin.schoolId != null) ? String(admin.schoolId)
      : (Array.isArray(admin?.schools) && admin.schools.length === 1) ? String(admin.schools[0].id)
      : '';
    setAdminToImpersonate(admin);
    setImpersonateSchoolId(fallbackSchoolId);
    setImpersonateDialogOpen(true);
  };

  const closeImpersonateDialog = () => {
    setImpersonateDialogOpen(false);
    setAdminToImpersonate(null);
    setImpersonateSchoolId('');
  };

  const confirmImpersonate = async () => {
    if (!adminToImpersonate?.id) return;
    try {
      const data = await fetchJson(`/api/school-admins/${adminToImpersonate.id}/impersonate`, {
        method: 'POST'
      });
      const alreadyImpersonating = !!localStorage.getItem('impersonationToken');
      const currentAccessToken = localStorage.getItem('accessToken');
      if (!alreadyImpersonating && currentAccessToken && !localStorage.getItem('impersonatorAccessToken')) {
        localStorage.setItem('impersonatorAccessToken', currentAccessToken);
        try { sessionStorage.setItem('impersonatorAccessToken', currentAccessToken); } catch (_) {}
      }
      const prevActiveSchoolId = localStorage.getItem('activeSchoolId') || '';
      const prevScopeSchoolId = localStorage.getItem('scopeSchoolId') || '';
      if (!localStorage.getItem('impersonatorActiveSchoolId')) {
        localStorage.setItem('impersonatorActiveSchoolId', prevActiveSchoolId);
      }
      if (!localStorage.getItem('impersonatorScopeSchoolId')) {
        localStorage.setItem('impersonatorScopeSchoolId', prevScopeSchoolId);
      }

      const selectedSchool = impersonateSchoolId ? String(impersonateSchoolId) : '';
      if (selectedSchool) {
        localStorage.setItem('activeSchoolId', selectedSchool);
        localStorage.setItem('scopeSchoolId', selectedSchool);
      }
      localStorage.removeItem('activeScopeLevel');
      localStorage.removeItem('scopeLevel');

      localStorage.setItem('impersonationToken', data.token);
      // IMPORTANT: Do NOT overwrite the SystemAdmin token.
      // fetchJson prioritizes `impersonationToken`, so this is sufficient for impersonated requests.
      try { localStorage.removeItem('user'); } catch (_) {}
      try { window.dispatchEvent(new Event('app:auth:changed')); } catch (_) {}
      window.location.href = '/school-overview';
    } catch (e) {
      setError(tr('schoolAdmins.errors.impersonate', 'Failed to impersonate admin'));
    } finally {
      closeImpersonateDialog();
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!schoolToToggleStatus?.id || !statusAction) return;
    const id = schoolToToggleStatus.id;

    const prevSchools = schools;
    const prevMetrics = metrics;
    const prevTotalCount = totalCount;

    const currentRow = schools.find((s) => s.id === id) || schoolToToggleStatus;
    const prevStatus = currentRow?.status;
    const nextStatus = statusAction === 'suspend' ? 'suspended' : 'active';

    setStatusDialogOpen(false);
    setSchoolToToggleStatus(null);
    setStatusAction(null);
    setError("");

    if (prevStatus && prevStatus !== nextStatus) {
      setSchools((prev) => prev.map((s) => (s.id === id ? { ...s, status: nextStatus } : s)));
      setMetrics((prev) => {
        const next = { ...(prev || {}) };
        if (prevStatus === 'active' && nextStatus === 'suspended') {
          next.active = Math.max(0, (next.active || 0) - 1);
          next.needsAttention = (next.needsAttention || 0) + 1;
        }
        if (prevStatus === 'suspended' && nextStatus === 'active') {
          next.needsAttention = Math.max(0, (next.needsAttention || 0) - 1);
          next.active = (next.active || 0) + 1;
        }
        return next;
      });
    }

    try {
      if (statusAction === 'suspend') {
        await fetchJson(`/schools/${id}/suspend`, {
          method: 'PUT',
        });
        showSnackbar(tr('schoolsPage.suspendSuccess', 'School suspended successfully'), 'success');
      } else {
        await fetchJson(`/schools/${id}/relieve-suspension`, {
          method: 'PUT',
        });
        showSnackbar(tr('schoolsPage.reactivateSuccess', 'School reactivated successfully'), 'success');
      }

      loadSchools();
      loadMetrics();
    } catch (e) {
      setSchools(prevSchools);
      setMetrics(prevMetrics);
      setTotalCount(prevTotalCount);

      const details = (e && e.data && (e.data.details || e.data.error))
        ? (e.data.details || e.data.error)
        : (e?.message || null);
      const baseMsg = statusAction === 'suspend'
        ? tr('schoolsPage.suspendError', 'Failed to suspend school')
        : tr('schoolsPage.reactivateError', 'Failed to reactivate school');
      setError(details ? `${baseMsg}: ${details}` : baseMsg);
    }
  };

const handleExport = async () => {
  try {
    const res = await fetchJson("/schools/export");
    const csv = res.csv || res;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute(
      "download",
      `schools_directory_${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    setError(t("schoolsPage.exportError") || "Failed to export data");
    console.error("Export error:", e);
  }
};

const openCreateDialog = () => {
  setEditingSchool(null);
  setDialogOpen(true);
  setError("");
};

const openEditDialog = (school) => {
  setEditingSchool(school);
  setDialogOpen(true);
  setError("");
};

const handleView = (school) => {
  const current = school || null;
  setSchoolToView(current);
  setViewDialogOpen(true);

  // Fetch richer details for view dialog
  const id = current?.id;
  if (!id) return;
  (async () => {
    try {
      const data = await fetchJson(`/schools/${id}/details`);
      if (data?.school) {
        setSchoolToView((prev) => {
          if (!prev || prev.id !== id) return prev;
          return { ...(prev || {}), ...(data.school || {}) };
        });
      }
    } catch (_) {
    }
  })();
};

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setSchoolToView(null);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingSchool(null);
  };

  const handleDelete = (id) => {
    const school = schools.find((s) => s.id === id);
    setSchoolToDelete(school);
    setDeleteDialogOpen(true);
  };

  const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000').replace(/\/$/, '');
  const resolveSchoolLogo = (logoUrl) => {
    if (!logoUrl) return '';
    const raw = String(logoUrl);
    if (!raw.trim()) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    return `${apiBase}${raw.startsWith('/') ? '' : '/'}${raw}`;
  };

  const columns = [
    {
      field: "logo",
      headerName: t("schoolsPage.logo") || "Logo",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        params?.row?.logoUrl ? (
          <Box
            component="img"
            src={resolveSchoolLogo(params.row.logoUrl)}
            alt={params.row.name || 'Logo'}
            sx={{
              width: 40,
              height: 40,
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: stringToColor(params?.row?.name || ""),
              fontSize: 14,
            }}
          >
            {params?.row?.name?.substring(0, 2) || "SC"}
          </Avatar>
        )
      ),
    },
    {
      field: "name",
      headerName: t("schoolsPage.name") || "Name",
      minWidth: 200,
      flex: 2,
      renderCell: (params) => params?.row ? params.value : null
    },
    {
      field: "phases",
      headerName: t("schoolsPage.phases") || "Phase / Section",
      minWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const phases = params?.row?.phases;
        if (!Array.isArray(phases) || phases.length === 0) return '—';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {phases.map((p) => (
              <Chip
                key={p.code}
                label={p.code === 'nur_pri' ? 'Nursery/Primary' : p.code === 'sec' ? 'Secondary' : p.name || p.code}
                size="small"
                color={p.code === 'nur_pri' ? 'primary' : 'secondary'}
                variant="outlined"
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "address",
      headerName: t("schoolsPage.address") || "Address",
      minWidth: 150,
      flex: 1.5,
      renderCell: (params) => params?.row ? params.value : null
    },
    {
      field: "adminDetails",
      headerName: t("schoolsPage.admin") || "Admin Contact",
      minWidth: 200,
      flex: 1.5,
      renderCell: (params) => (
        params?.row ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
              {params.row.adminName}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}
            >
              {params.row.adminEmail}
            </Typography>
          </Box>
        ) : null
      ),
    },
    {
      field: "status",
      headerName: t("schoolsPage.status") || "Status",
      width: 100,
      renderCell: (params) => (
        params?.row ? (
          <Chip
            label={params.value}
            color={params.value === "active" ? "success" : "error"}
            size="small"
          />
        ) : null
      ),
    },
    {
      field: "subscriptionStatus",
      headerName: t("schools.subscription") || t("schoolsPage.subscription") || "Subscription",
      width: 120,
      renderCell: (params) => (
        params?.row ? (
          <Chip
            label={params.value}
            color={
              params.value === 'active'
                ? 'success'
                : params.value === 'in_grace'
                  ? 'warning'
                  : params.value === 'expired'
                    ? 'error'
                    : params.value === 'suspended'
                      ? 'error'
                      : 'default'
            }
            size="small"
            variant={params.value === 'in_grace' ? 'outlined' : 'filled'}
          />
        ) : null
      ),
    },
    {
      field: "onboardingStage",
      headerName: t("schoolsPage.onboarding") || "Onboarding",
      width: 120,
      renderCell: (params) => (
        params?.row ? (
          <div style={{ display: "flex", alignItems: "center", width: '100%' }}>
            <div
              style={{
                width: "100%",
                backgroundColor: theme.palette.grey[300],
                borderRadius: 4,
                marginRight: 8,
              }}
            >
              <div
                style={{
                  width: `${params.row.onboardingProgress || 0}%`,
                  height: 6,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 4,
                }}
              />
            </div>
            <span style={{ fontSize: '0.75rem' }}>{params.row.onboardingProgress || 0}%</span>
          </div>
        ) : null
      ),
    },
    {
      field: "updatedAt",
      headerName: t("schoolsPage.lastUpdated") || "Last Updated",
      width: 120,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: "actions",
      headerName: t("common.actions") || "Actions",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <ActionsCell
          params={params}
          openEditDialog={openEditDialog}
          handleDelete={handleDelete}
          handleView={handleView}
          handleSuspend={handleSuspend}
          handleRelieveSuspension={handleRelieveSuspension}
          handleRenewSubscription={handleRenewSubscription}
          openPricingDialog={openPricingDialog}
          handleUnsubscribe={handleUnsubscribe}
          user={user}
          theme={theme}
          t={t}
        />
      ),
    },
  ];

  const schoolAdminsColumns = [
    {
      field: 'name',
      headerName: tr('schoolAdmins.columns.name', 'Name'),
      flex: 1,
      renderCell: (params) => (
        params?.row ? (
          <Stack>
            <Typography fontWeight="medium">{params.value}</Typography>
            <Typography variant="body2" color="textSecondary">
              {params.row.email}
            </Typography>
          </Stack>
        ) : null
      )
    },
    {
      field: 'schools',
      headerName: tr('schoolAdmins.columns.school', 'School(s)'),
      flex: 2,
      renderCell: (params) => {
        if (Array.isArray(params?.row?.schools) && params.row.schools.length > 0) {
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {params.row.schools.map((school) => (
                <Chip
                  key={school.id}
                  label={school.name + (school.status ? ` (${school.status})` : '')}
                  size="small"
                  color={school.status === 'active' ? 'success' : (school.status === 'suspended' ? 'warning' : 'default')}
                />
              ))}
            </Stack>
          );
        }
        return <Typography variant="body2" color="textSecondary">N/A</Typography>;
      }
    },
    {
      field: 'status',
      headerName: tr('schoolAdmins.columns.status', 'Status'),
      width: 140,
      renderCell: (params) => (
        params?.row ? (
          <Chip
            label={params.row.status}
            color={params.row.status === 'active' ? 'success' : (params.row.status === 'suspended' ? 'warning' : 'default')}
            size="small"
          />
        ) : null
      )
    },
    {
      field: 'actions',
      headerName: tr('common.actions', 'Actions'),
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<LockIcon />}
            onClick={() => openImpersonateDialog(params.row)}
            disabled={user?.role !== 'SystemAdmin'}
          >
            {tr('schoolAdmins.actions.impersonate', 'Impersonate')}
          </Button>
        </Box>
      )
    }
  ];

  return (
    <Box width="100%"
      sx={{
        p: { xs: 2, md: 4 },
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        
      }}
    >
      <Typography variant="h4" gutterBottom>
        {t("directoryPage.title") || "School Directory"}
      </Typography>

      <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2 }}>
        <Tab label={tr('directoryPage.tabs.schools', 'Schools')} />
        <Tab label={tr('directoryPage.tabs.schoolAdmins', 'School Admins')} />
      </Tabs>

      {/* Metrics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                {t("directoryPage.totalSchools") || "Total Schools"}
              </Typography>
              <Typography variant="h4">{metrics.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                {t("directoryPage.activeSchools") || "Active Schools"}
              </Typography>
              <Typography variant="h4" color="success.main">
                {metrics.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                {t("directoryPage.pendingOnboarding") || "Pending Onboarding"}
              </Typography>
              <Typography variant="h4" color="warning.main">
                {metrics.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                {t("directoryPage.needsAttention") || "Needs Attention"}
              </Typography>
              <Typography variant="h4" color="error.main">
                {metrics.needsAttention}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder={t("directoryPage.search") || "Search schools..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Subscription</InputLabel>
            <Select
              value={subscriptionStatusFilter}
              label="Subscription"
              onChange={(e) => {
                setSubscriptionStatusFilter(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="in_grace">In Grace</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadSchools();
              loadMetrics();
            }}
          >
            {t("directoryPage.refresh") || "Refresh"}
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
          >
            {t("directoryPage.export") || "Export CSV"}
          </Button>

          {user?.role === "SystemAdmin" && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              {t("directoryPage.addSchool") || "Add School"}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ErrorBoundary>
        {activeTab === 0 ? (
          <Box sx={{ flex: 1, height: "100%", width: '100%', overflowX: 'auto' }}>
            <Box sx={{ minWidth: { xs: 1100, lg: '100%' }, height: "100%" }}>
              <DataGrid
                rows={schools}
                columns={columns}
                pageSizeOptions={[5, 10, 25, 50]}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                rowCount={totalCount}
                loading={loading}
                rowHeight={75}
                autosizeOnMount
                autosizeOptions={{
                  includeHeaders: true,
                  includeOutliers: true,
                  expand: true,
                }}
                sx={{
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e0e0e0",
                  },
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, height: "100%", width: '100%', overflowX: 'auto' }}>
            {adminsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {adminsError}
              </Alert>
            )}
            {adminsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ minWidth: { xs: 980, lg: '100%' }, height: "100%" }}>
                <DataGrid
                  rows={schoolAdmins}
                  columns={schoolAdminsColumns}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                  rowHeight={75}
                  autosizeOnMount
                  autosizeOptions={{
                    includeHeaders: true,
                    includeOutliers: true,
                    expand: true,
                  }}
                  sx={{
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid #f0f0f0",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f8f9fa",
                      borderBottom: "2px solid #e0e0e0",
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </ErrorBoundary>

      <Box sx={{ mt: 2, textAlign: "center", color: "#757575" }}>
        <Typography variant="body2">
          {activeTab === 0
            ? (t("directoryPage.showing", {
                count: schools.length,
                total: totalCount,
              }) || `Showing ${schools.length} of ${totalCount} schools`)
            : (t("directoryPage.showingAdmins", {
                count: schoolAdmins.length,
              }) || `Showing ${schoolAdmins.length} school admins`)
          }
        </Typography>
      </Box>

      <SchoolFormModal
        open={dialogOpen}
        onClose={closeDialog}
        onSave={() => {
          loadSchools();
          loadMetrics();
          closeDialog();
        }}
        school={editingSchool}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSchoolToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={tr("schoolsPage.confirmDeleteTitle", "Delete School")}
        message={tr("schoolsPage.confirmDelete", "Are you sure you want to delete this school?")}
        warning={tr("schoolsPage.deleteCascadeWarning", "Deleting a school will also delete all related data (users, students, exams, etc). This action cannot be undone.")}
        confirmText={tr("common.delete", "Delete")}
        cancelText={tr("common.cancel", "Cancel")}
      />

      <ConfirmDeleteDialog
        open={statusDialogOpen}
        onClose={() => {
          setStatusDialogOpen(false);
          setSchoolToToggleStatus(null);
          setStatusAction(null);
        }}
        onConfirm={handleConfirmStatusChange}
        title={
          statusAction === 'suspend'
            ? tr('schoolsPage.confirmSuspendTitle', 'Suspend School')
            : tr('schoolsPage.confirmReactivateTitle', 'Reactivate School')
        }
        message={
          statusAction === 'suspend'
            ? tr('schoolsPage.confirmSuspend', `Suspend ${schoolToToggleStatus?.name || 'this school'}?`)
            : tr('schoolsPage.confirmReactivate', `Reactivate ${schoolToToggleStatus?.name || 'this school'}?`)
        }
        warning={
          statusAction === 'suspend'
            ? tr('schoolsPage.suspendWarning', 'This will suspend the school and all its users/subscription access.')
            : null
        }
        confirmText={statusAction === 'suspend' ? tr('schoolsPage.suspend', 'Suspend') : tr('schoolsPage.reactivate', 'Reactivate')}
        confirmColor={statusAction === 'suspend' ? 'warning' : 'success'}
      />

      <Dialog open={viewDialogOpen} onClose={closeViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>{schoolToView?.name || ''}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={schoolToView?.logoUrl}
                sx={{ width: 56, height: 56, bgcolor: stringToColor(schoolToView?.name || "") }}
              >
                {schoolToView?.name?.substring(0, 2) || 'SC'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {schoolToView?.name || ''}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {tr('schoolsPage.schoolCode', 'School Code')}: {schoolToView?.code || 'N/A'}
                </Typography>
              </Box>
              <Chip
                label={schoolToView?.status || 'N/A'}
                color={schoolToView?.status === 'active' ? 'success' : schoolToView?.status === 'suspended' ? 'error' : 'default'}
                size="small"
                sx={{ ml: 'auto' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                {tr('schoolsPage.address', 'Address')}
              </Typography>
              <Typography variant="body1">{schoolToView?.address || 'N/A'}</Typography>
            </Grid>

            {/* School Credentials / Authenticity */}
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                {tr('directoryPage.credentials', 'School Credentials')}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" color="textSecondary">
                {tr('directoryPage.region', 'Region')}
              </Typography>
              <Typography variant="body1">{schoolToView?.region || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" color="textSecondary">
                {tr('directoryPage.town', 'Town / City')}
              </Typography>
              <Typography variant="body1">{schoolToView?.town || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" color="textSecondary">
                {tr('directoryPage.schoolType', 'School Type')}
              </Typography>
              <Typography variant="body1">
                {({ private: 'Private (Lay)', confessional: 'Confessional', public: 'Public' })[schoolToView?.schoolType] || schoolToView?.schoolType || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" color="textSecondary">
                {tr('directoryPage.founderName', 'Founder / Proprietor')}
              </Typography>
              <Typography variant="body1">{schoolToView?.founderName || 'N/A'}</Typography>
            </Grid>
            {schoolToView?.founderNationalId && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  {tr('directoryPage.founderNationalId', 'Founder National ID (CNI)')}
                </Typography>
                <Typography variant="body1">{schoolToView.founderNationalId}</Typography>
              </Grid>
            )}
            {Array.isArray(schoolToView?.arreteDocuments) && schoolToView.arreteDocuments.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                  {tr('directoryPage.arrete', "Arrêté d'Ouverture (Opening Order)")}
                </Typography>
                {schoolToView.arreteDocuments.map((doc, idx) => {
                  const ministryLabels = { MINEDUB: 'MINEDUB (Basic Education)', MINESEC: 'MINESEC (Secondary Education)', MINESUP: 'MINESUP (Higher Education)', MINEFOP: 'MINEFOP (Vocational/Technical)' };
                  const phaseLabels = { nur_pri: 'Nursery & Primary', sec: 'Secondary' };
                  return (
                    <Box key={idx} sx={{ mb: 1, p: 1.5, bgcolor: '#F5F7FA', borderRadius: 1, border: '1px solid #E3F2FD' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {phaseLabels[doc.phaseCode] || doc.phaseCode} — {ministryLabels[doc.ministry] || doc.ministry}
                      </Typography>
                      <Typography variant="body2">
                        {tr('directoryPage.arreteRef', 'Reference')}: <strong>{doc.referenceNumber || '—'}</strong>
                        {doc.issueDate ? ` | ${tr('directoryPage.arreteDate', 'Date')}: ${new Date(doc.issueDate).toLocaleDateString()}` : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>Subscription & Plan</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">Plan</Typography>
              <Typography variant="body1">
                {schoolToView?.subscription?.plan?.name
                  || (schoolToView?.subscription?.planId ? `Plan #${schoolToView.subscription.planId}` : 'N/A')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">Sub. Status</Typography>
              <Typography variant="body1">
                {schoolToView?.subscription?.status || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">Period</Typography>
              <Typography variant="body1">
                {schoolToView?.subscription?.startDate ? new Date(schoolToView.subscription.startDate).toLocaleDateString() : '?'}
                {' — '}
                {schoolToView?.subscription?.endDate ? new Date(schoolToView.subscription.endDate).toLocaleDateString() : '?'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>Usage</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" color="textSecondary">Students</Typography>
              <Typography variant="body1" fontWeight={600}>
                {Number.isFinite(Number(schoolToView?.studentsCount)) ? Number(schoolToView?.studentsCount) : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" color="textSecondary">Users</Typography>
              <Typography variant="body1" fontWeight={600}>
                {Number.isFinite(Number(schoolToView?.usersCount)) ? Number(schoolToView?.usersCount) : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" color="textSecondary">Phases</Typography>
              <Typography variant="body1">
                {Array.isArray(schoolToView?.phases) && schoolToView.phases.length
                  ? schoolToView.phases.map((p) => p.name || p.code).join(', ')
                  : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" color="textSecondary">Country</Typography>
              <Typography variant="body1">{schoolToView?.country || 'N/A'}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>Admin Contact</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">Name</Typography>
              <Typography variant="body1">{schoolToView?.adminName || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">Email</Typography>
              <Typography variant="body1">{schoolToView?.adminEmail || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
              <Typography variant="body1">{schoolToView?.adminPhone || 'N/A'}</Typography>
            </Grid>

            {Array.isArray(schoolToView?.recentPayments) && schoolToView.recentPayments.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>Recent Payments</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                    {schoolToView.recentPayments.map((p, i) => (
                      <Box key={p.id || i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                        <Typography variant="body2">
                          {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                        </Typography>
                        <Typography variant="body2">{p.method || '—'}</Typography>
                        <Chip label={p.status || 'unknown'} size="small" color={p.status === 'completed' ? 'success' : p.status === 'pending' ? 'warning' : 'default'} />
                        <Typography variant="body2" fontWeight={600}>
                          {p.amount != null ? `${Number(p.amount).toLocaleString()} XAF` : '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewDialog}>{tr('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={impersonateDialogOpen} onClose={closeImpersonateDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{tr('schoolAdmins.actions.impersonate', 'Impersonate')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            {tr('schoolAdmins.confirmImpersonate', 'You are about to impersonate this School Admin:')}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
            {adminToImpersonate?.name || ''}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {adminToImpersonate?.email || ''}
          </Typography>

          {Array.isArray(adminToImpersonate?.schools) && adminToImpersonate.schools.length > 1 && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{tr('schoolAdmins.selectSchool', 'Select School')}</InputLabel>
                <Select
                  value={impersonateSchoolId}
                  label={tr('schoolAdmins.selectSchool', 'Select School')}
                  onChange={(e) => setImpersonateSchoolId(e.target.value)}
                >
                  {adminToImpersonate.schools.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImpersonateDialog}>{tr('common.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            onClick={confirmImpersonate}
            disabled={!adminToImpersonate || (Array.isArray(adminToImpersonate?.schools) && adminToImpersonate.schools.length > 1 && !impersonateSchoolId)}
          >
            {tr('schoolAdmins.actions.impersonate', 'Impersonate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PER-SCHOOL SUBSCRIPTION TERMS.
          Every school used to be billed the platform rate on the academic year,
          per student, with no way to record a flat fee or a negotiated rate for
          one school. Leaving the mode on "standard" clears any terms and puts
          the school back on that platform rate — which is why clearing is an
          explicit choice here rather than an empty price box. */}
      <Dialog open={Boolean(pricingSchool)} onClose={() => setPricingSchool(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {tr('schoolsPage.pricing.title', 'Subscription pricing')}
          {pricingSchool ? ` — ${pricingSchool.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {tr('schoolsPage.pricing.help',
              'Set what this school pays. Leave it on the standard rate to bill it the same as every other school.')}
          </Typography>

          <TextField
            select
            fullWidth
            margin="normal"
            label={tr('schoolsPage.pricing.mode', 'Billing basis')}
            value={pricingMode}
            onChange={(e) => setPricingMode(e.target.value)}
          >
            <MenuItem value="">{tr('schoolsPage.pricing.modeStandard', 'Standard rate (same as all schools)')}</MenuItem>
            <MenuItem value="per_student">{tr('schoolsPage.pricing.modePerStudent', 'Per student')}</MenuItem>
            <MenuItem value="one_time">{tr('schoolsPage.pricing.modeOneTime', 'One-time fee')}</MenuItem>
          </TextField>

          {Boolean(pricingMode) && (
            <TextField
              fullWidth
              margin="normal"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              label={pricingMode === 'one_time'
                ? tr('schoolsPage.pricing.amountOneTime', 'Flat fee (XAF)')
                : tr('schoolsPage.pricing.amountPerStudent', 'Rate per student (XAF)')}
              value={pricingAmount}
              onChange={(e) => setPricingAmount(e.target.value)}
              helperText={pricingMode === 'one_time'
                ? tr('schoolsPage.pricing.helpOneTime', 'Charged once per academic year, whatever the size of the school.')
                : tr('schoolsPage.pricing.helpPerStudent', 'Multiplied by the number of enrolled students each academic year.')}
            />
          )}

          <TextField
            fullWidth
            margin="normal"
            multiline
            minRows={2}
            label={tr('schoolsPage.pricing.note', 'Note (internal)')}
            value={pricingNote}
            onChange={(e) => setPricingNote(e.target.value)}
            helperText={tr('schoolsPage.pricing.noteHelp', 'Why these terms were agreed. Never shown to the school.')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingSchool(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={savePricing}
            disabled={pricingSaving || (Boolean(pricingMode) && !(Number(pricingAmount) >= 0))}
          >
            {pricingSaving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
